/**
 * ProductivityContext — single source of truth for Notes + Tasks across the
 * floating dock AND the /tools hub. CRUD operations update local state
 * optimistically and sync to the server.
 *
 * Calculator state is purely local (no server) — it lives in its own component.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Ctx = createContext(null);

export function ProductivityProvider({ children }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refreshNotes = useCallback(async ({ archived = false, q, tag } = {}) => {
    try {
      const params = new URLSearchParams();
      params.set("archived", String(archived));
      if (q) params.set("q", q);
      if (tag) params.set("tag", tag);
      const { data } = await api.get(`/notes?${params.toString()}`);
      setNotes(data.notes || []);
    } catch (_) {
      setNotes([]);
    }
  }, []);

  const refreshTasks = useCallback(async ({ archived = false, q, tag } = {}) => {
    try {
      const params = new URLSearchParams();
      params.set("archived", String(archived));
      if (q) params.set("q", q);
      if (tag) params.set("tag", tag);
      const { data } = await api.get(`/tasks?${params.toString()}`);
      setTasks(data.tasks || []);
    } catch (_) {
      setTasks([]);
    }
  }, []);

  useEffect(() => {
    if (!user || user.role === "owner") {
      setLoaded(true);
      return;
    }
    (async () => {
      await Promise.all([refreshNotes(), refreshTasks()]);
      setLoaded(true);
    })();
  }, [user, refreshNotes, refreshTasks]);

  // ----- Notes ----------------------------------------------------------
  const createNote = useCallback(async (payload = {}) => {
    const { data } = await api.post("/notes", { color: "yellow", title: "", body: "", ...payload });
    setNotes((prev) => [data, ...prev]);
    return data;
  }, []);

  const updateNote = useCallback(async (id, patch) => {
    // optimistic
    setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, ...patch } : n)));
    try {
      const { data } = await api.patch(`/notes/${id}`, patch);
      setNotes((prev) => prev.map((n) => (n.id === id ? data : n)));
      return data;
    } catch (e) {
      // rollback on failure by refetching
      await refreshNotes();
      throw e;
    }
  }, [refreshNotes]);

  const deleteNote = useCallback(async (id) => {
    setNotes((prev) => prev.filter((n) => n.id !== id));
    try { await api.delete(`/notes/${id}`); } catch (_) { await refreshNotes(); }
  }, [refreshNotes]);

  // ----- Tasks ----------------------------------------------------------
  const createTask = useCallback(async (payload) => {
    const { data } = await api.post("/tasks", { priority: "med", tags: [], ...payload });
    setTasks((prev) => [data, ...prev]);
    return data;
  }, []);

  const updateTask = useCallback(async (id, patch) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    try {
      const { data } = await api.patch(`/tasks/${id}`, patch);
      setTasks((prev) => prev.map((t) => (t.id === id ? data : t)));
      return data;
    } catch (e) {
      await refreshTasks();
      throw e;
    }
  }, [refreshTasks]);

  const deleteTask = useCallback(async (id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    try { await api.delete(`/tasks/${id}`); } catch (_) { await refreshTasks(); }
  }, [refreshTasks]);

  const value = useMemo(() => ({
    notes, tasks, loaded,
    refreshNotes, refreshTasks,
    createNote, updateNote, deleteNote,
    createTask, updateTask, deleteTask,
  }), [notes, tasks, loaded, refreshNotes, refreshTasks, createNote, updateNote, deleteNote, createTask, updateTask, deleteTask]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useProductivity() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useProductivity must be used inside ProductivityProvider");
  return ctx;
}
